import { computed, InjectionToken, signal, Signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ProfileService } from '@tfs/auth';
import { CampaignsApi } from '@tfs/tool-library-library-api';

import { NpsCampaignSimulatorConfig } from '../../environment.config';

export interface NpsCampaign {
  id: string;
  name: string;
  description: string;
}

export interface NpsCampaignBackend {
  readonly shouldShowCampaign: Signal<boolean>;
  getCampaign(): Observable<NpsCampaign | null>;
  submitResponse(
    campaignId: string,
    score: number | null,
    comment: string | null
  ): Observable<void>;
}

export const NPS_CAMPAIGN_BACKEND = new InjectionToken<NpsCampaignBackend>('NPS_CAMPAIGN_BACKEND');

export class NpsCampaignSimulator implements NpsCampaignBackend {
  private readonly config: NpsCampaignSimulatorConfig;
  readonly shouldShowCampaign: Signal<boolean>;

  constructor(config: NpsCampaignSimulatorConfig) {
    this.config = config;
    this.shouldShowCampaign = signal(config.campaignActive);
  }

  getCampaign(): Observable<NpsCampaign | null> {
    if (!this.config.campaignActive) {
      return of(null);
    }

    return of({
      id: 'simulator-campaign',
      name: this.config.campaignName,
      description: this.config.campaignDescription,
    });
  }

  submitResponse(
    campaignId: string,
    score: number | null,
    comment: string | null
  ): Observable<void> {
    console.log('[NPS Simulator] Campaign response:', { campaignId, score, comment });
    return of<void>(undefined);
  }
}

export class NpsCampaignRealBackend implements NpsCampaignBackend {
  private readonly campaignsApi: CampaignsApi;
  readonly shouldShowCampaign: Signal<boolean>;

  constructor(campaignsApi: CampaignsApi, profileService: ProfileService) {
    this.campaignsApi = campaignsApi;
    this.shouldShowCampaign = computed(() => profileService.hasActiveCampaign());
  }

  getCampaign(): Observable<NpsCampaign | null> {
    return this.campaignsApi.getCampaigns().pipe(
      map(campaigns => {
        const active = campaigns.find(c => c.isActive && c.id);
        if (!active?.id) {
          return null;
        }

        return {
          id: active.id,
          name: active.name ?? '',
          description: active.description ?? '',
        };
      })
    );
  }

  submitResponse(
    campaignId: string,
    score: number | null,
    comment: string | null
  ): Observable<void> {
    return this.campaignsApi.postCampaignResponse({
      campaignId,
      score: score as number,
      comment: comment?.trim() || null,
    }) as Observable<void>;
  }
}
